import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import client, { tryParseJSON } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BookOpen, Save, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TRANSLATIONS = ["KJV", "WEB", "ASV"];

export default function VerseLookup() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState(Number(params.get("book")) || 43);
  const [chapter, setChapter] = useState(Number(params.get("chapter")) || 3);
  const [verse, setVerse] = useState(Number(params.get("verse")) || 16);
  const [translation, setTranslation] = useState(params.get("translation") || "KJV");

  const [busy, setBusy] = useState(false);
  const [verseData, setVerseData] = useState(null);
  const [interlinear, setInterlinear] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [strongEntry, setStrongEntry] = useState(null);
  const [strongBusy, setStrongBusy] = useState(false);
  const [syntax, setSyntax] = useState(null);
  const [syntaxBusy, setSyntaxBusy] = useState(false);

  useEffect(() => {
    client.get("/bible/books").then((r) => setBooks(r.data.books)).catch(() => {});
  }, []);

  const load = async () => {
    setBusy(true); setVerseData(null); setInterlinear(null);
    setSelectedToken(null); setStrongEntry(null); setSyntax(null);
    try {
      const [v, i] = await Promise.all([
        client.get("/bible/verse", { params: { book: bookId, chapter, verse, translation } }),
        client.get("/bible/interlinear", { params: { book: bookId, chapter, verse } }),
      ]);
      setVerseData(v.data);
      setInterlinear(i.data);
      setParams({ book: String(bookId), chapter: String(chapter), verse: String(verse), translation });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verse not found");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openToken = async (tok) => {
    setSelectedToken(tok);
    setStrongEntry(null); setSyntax(null);
    if (!tok.strongs?.length) return;
    setStrongBusy(true);
    try {
      // Determine G or H based on testament
      const book = books.find((b) => b.id === bookId);
      const prefix = book && book.testament === "NT" ? "G" : "H";
      const strong = `${prefix}${tok.strongs[0]}`;
      const r = await client.get(`/bible/dictionary/${strong}`);
      setStrongEntry({ ...r.data, prefix });
    } catch (err) {
      // ignore
    } finally { setStrongBusy(false); }
  };

  const analyzeSyntax = async () => {
    if (!selectedToken) return;
    if (!user) { toast.error("Sign in to use AI analysis"); nav("/login"); return; }
    setSyntaxBusy(true);
    try {
      const book = books.find((b) => b.id === bookId);
      const prefix = book && book.testament === "NT" ? "G" : "H";
      const strong = selectedToken.strongs?.[0] ? `${prefix}${selectedToken.strongs[0]}` : null;
      const r = await client.post("/study/syntax", {
        word: selectedToken.text,
        strong,
        language: book?.testament === "NT" ? "Greek" : "Hebrew",
        verse_reference: `${verseData.book} ${chapter}:${verse}`,
      });
      setSyntax(tryParseJSON(r.data.raw) || { raw: r.data.raw });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setSyntaxBusy(false); }
  };

  const saveStudy = async () => {
    if (!user) { toast.error("Sign in to save"); nav("/login"); return; }
    try {
      await client.post("/studies", {
        title: `${verseData.book} ${chapter}:${verse} (${translation})`,
        kind: "verse",
        payload: {
          reference: { book_id: bookId, book: verseData.book, chapter, verse, translation },
          text: verseData.text,
          interlinear: interlinear?.tokens || [],
        },
      });
      toast.success("Study saved");
    } catch (err) { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-10" data-testid="verse-lookup-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#78350F] mb-4">Interlinear Verse Lookup</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-stone-900 mb-3">A verse, word by word.</h1>
        <p className="text-stone-600 max-w-2xl">Choose a passage. Read the English above the original Hebrew or Greek. Click any word to open its Strong's entry and its grammatical parsing.</p>
      </div>

      {/* Selector */}
      <div className="border-t border-b hairline py-6 grid grid-cols-2 md:grid-cols-5 gap-6 items-end">
        <div className="col-span-2 md:col-span-2">
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Book</label>
          <select value={bookId} onChange={(e) => setBookId(Number(e.target.value))} data-testid="verse-book"
            className="w-full bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] py-2 text-lg font-serif-display">
            {books.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Chapter</label>
          <input type="number" min="1" value={chapter} onChange={(e) => setChapter(Number(e.target.value))} data-testid="verse-chapter"
            className="w-full bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] py-2 text-lg font-serif-display" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Verse</label>
          <input type="number" min="1" value={verse} onChange={(e) => setVerse(Number(e.target.value))} data-testid="verse-verse"
            className="w-full bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] py-2 text-lg font-serif-display" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Version</label>
          <select value={translation} onChange={(e) => setTranslation(e.target.value)} data-testid="verse-translation"
            className="w-full bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] py-2 text-lg font-serif-display">
            {TRANSLATIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="col-span-2 md:col-span-5">
          <button onClick={load} disabled={busy} data-testid="verse-load"
            className="bg-[#9A3B3B] hover:bg-[#7F2F2F] text-white px-8 py-3 tracking-wide transition-colors disabled:opacity-60">
            {busy ? "Loading..." : "Load verse"}
          </button>
        </div>
      </div>

      {busy && <div className="text-center py-12 text-stone-500"><Loader2 className="mx-auto animate-spin" /></div>}

      {verseData && !busy && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            {/* English translation */}
            <div className="border hairline bg-[#FDFBF7] p-8 md:p-10" data-testid="verse-text-card">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-stone-500">{translation}</div>
                  <div className="font-serif-display text-3xl text-stone-900">{verseData.book} {chapter}:{verse}</div>
                </div>
                <button onClick={saveStudy} data-testid="verse-save"
                  className="text-sm text-[#2E4F4F] hover:text-[#213A3A] flex items-center gap-1 border border-[#2E4F4F] px-3 py-1.5">
                  <Save size={13} strokeWidth={1.5} /> Save
                </button>
              </div>
              <p className="font-serif-display text-2xl md:text-3xl leading-relaxed text-stone-800">
                {verseData.text}
              </p>
            </div>

            {/* Interlinear */}
            <div className="border hairline bg-[#FDFBF7] p-8 md:p-10" data-testid="interlinear-card">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-6">Interlinear (KJV + Strong's)</div>
              {interlinear?.tokens?.length ? (
                <div className="flex flex-wrap gap-x-1 gap-y-4" data-testid="interlinear-tokens">
                  {interlinear.tokens.map((tok, i) => (
                    <button key={i} onClick={() => openToken(tok)}
                      data-testid={`token-${i}`}
                      className={`ilw ${selectedToken === tok ? "bg-[#F3EFE6]" : ""}`}>
                      <span className="font-serif-display text-lg text-stone-900">{tok.text}</span>
                      {tok.strongs.length > 0 && (
                        <span className="text-[10px] uppercase tracking-wider text-amber-800 bg-amber-100/50 px-1">
                          {(books.find((b) => b.id === bookId)?.testament === "NT" ? "G" : "H")}{tok.strongs[0]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-stone-500 italic">No Strong's tagging available for this verse.</div>
              )}
            </div>
          </div>

          {/* Side panel */}
          <aside className="space-y-6">
            {selectedToken ? (
              <div className="border hairline bg-[#FDFBF7] p-6 sticky top-24" data-testid="token-panel">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500">Selected word</div>
                    <div className="font-serif-display text-3xl text-stone-900 mt-1">{selectedToken.text}</div>
                  </div>
                  <button onClick={() => setSelectedToken(null)} data-testid="close-token">
                    <X size={16} className="text-stone-500" />
                  </button>
                </div>

                {selectedToken.strongs?.length > 0 && (
                  <div className="mb-6">
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Strong's</div>
                    <div className="font-mono text-sm text-[#9A3B3B]">
                      {selectedToken.strongs.map((s) => `${(books.find((b) => b.id === bookId)?.testament === "NT" ? "G" : "H")}${s}`).join(", ")}
                    </div>
                  </div>
                )}

                {strongBusy && <Loader2 className="animate-spin text-stone-400" size={16} />}

                {strongEntry && strongEntry.found && (
                  <div className="border-t hairline pt-4 space-y-3" data-testid="strong-entry">
                    {strongEntry.lexeme && (
                      <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">Original</div>
                        <div className="font-serif-display text-2xl text-stone-900" style={{ fontFamily: "'SBL Hebrew','SBL Greek','Cormorant Garamond',serif" }}>
                          {strongEntry.lexeme}
                        </div>
                      </div>
                    )}
                    {strongEntry.transliteration && (
                      <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">Transliteration</div>
                        <div className="italic text-stone-700">{strongEntry.transliteration}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">Language</div>
                      <div className="text-sm text-stone-700">{strongEntry.language}</div>
                    </div>
                    {strongEntry.definition && (
                      <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">Definition</div>
                        <div className="text-sm text-stone-700 leading-relaxed">{strongEntry.definition}</div>
                      </div>
                    )}
                  </div>
                )}

                {selectedToken.strongs?.length > 0 && (
                  <div className="border-t hairline pt-4 mt-4">
                    <button onClick={analyzeSyntax} disabled={syntaxBusy} data-testid="analyze-syntax"
                      className="w-full border border-[#2E4F4F] text-[#2E4F4F] hover:bg-stone-100 px-4 py-2 text-sm tracking-wide transition-colors disabled:opacity-60">
                      {syntaxBusy ? "Analyzing..." : "Analyze syntax with AI"}
                    </button>

                    {syntax && (
                      <div className="mt-4 border-t hairline pt-4 space-y-3" data-testid="syntax-panel">
                        <div className="text-xs uppercase tracking-[0.25em] text-stone-500">Morphology</div>
                        {syntax.parsing ? (
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {Object.entries(syntax.parsing).map(([k, v]) => v && (
                              <div key={k} className="border-t hairline pt-2">
                                <div className="uppercase tracking-wider text-stone-500 text-[10px]">{k}</div>
                                <div className="text-stone-800">{v}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                        {syntax.explanation && (
                          <p className="text-sm text-stone-700 leading-relaxed border-t hairline pt-3">{syntax.explanation}</p>
                        )}
                        {!syntax.parsing && syntax.raw && (
                          <pre className="text-xs text-stone-600 whitespace-pre-wrap">{syntax.raw}</pre>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="border hairline bg-[#FDFBF7] p-6 text-sm text-stone-500 italic sticky top-24">
                <BookOpen size={16} strokeWidth={1.5} className="mb-3 text-stone-400" />
                Click any word in the interlinear panel to see its Strong's entry and morphology.
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
